import geometryCenter from '@turf/center';

import {OBJ, Obj, SIGNAL, SLOT} from '../../../obj';
import {bind, isNumber} from '../../../util';
import {IFilterListOpt, svc} from '../../../request';
import {getLogger} from '../../../logging';
import {ProjectDetailView} from '../view';
import {FilterBox} from './filterbox';
import {singleParcelUidFromAddressFilter} from './filterlist';
import {ElObj} from '../../../elobj';
import {InteractiveMapControlMode} from '../../../constants';
import {GeoCoordinate, Point} from '../../../tools';
import {Menu} from '../../../ui/menu';
import {UndoCommand} from '../../../ui/undostack';

const logger = getLogger('projectdetail.filtermanager');

@OBJ
export class FilterManager extends Obj {
	private disabled: boolean;
	private expressionCapabilities: {[field: string]: IProjectFilterCapabilityField};
	private filterCapabilityWarnings: Array<IFilterCapabilityWarning>;
	private expressionToken: IExpressionToken;
	private filterBox: FilterBox | null;
	private filterBoxInline: boolean;
	private filterCapabilityRefreshSeq: number;
	private initPromise: Promise<void>;
	private identFilterPkMap: Map<number, number>;
	private movingToFilter: IFilter | null;
	private pendingMapClickTimer: number | null;
	private polygonMenu: Menu | null;
	private polygonMenuActions: Array<() => void>;
	private shapeEditingFilterId: number | null;
	private view: ProjectDetailView;

	constructor(view: ProjectDetailView) {
		super();
		this.disabled = false;
		this.expressionCapabilities = {};
		this.filterCapabilityWarnings = [];
		this.expressionToken = {attributes: [], operators: []};
		this.filterBox = null;
		this.filterBoxInline = false;
		this.filterCapabilityRefreshSeq = 0;
		this.initPromise = Promise.resolve();
		this.identFilterPkMap = new Map<number, number>();
		this.movingToFilter = null;
		this.pendingMapClickTimer = null;
		this.polygonMenu = null;
		this.polygonMenuActions = [];
		this.shapeEditingFilterId = null;
		this.view = view;
		this.initPromise = this.init();
	}

	private addConnections(): void {
		Obj.connect(
			this, 'beginShapeEditing',
			this, 'closeFilterBox');
		Obj.connect(
			this, 'filterCreated',
			this, '_filtersCreated');
		Obj.connect(
			this, 'filterDeleted',
			this, '_filterDeleted');
		Obj.connect(
			this, 'filterUpdated',
			this, '_filterUpdated');
		Obj.connect(
			this.view, 'placeAutoCompleteActivated',
			this, 'placeAutoCompleteActivated');
		Obj.connect(
			this.view.map, 'moveEnded',
			this, 'mapMoveEnded');
		Obj.connect(
			this.view.map, 'featuresCreated',
			this, 'mapFeaturesCreated');
		Obj.connect(
			this.view.map, 'featuresDeleted',
			this, 'mapFeaturesDeleted');
		Obj.connect(
			this.view.map, 'featuresUpdated',
			this, 'mapFeaturesUpdated');
		Obj.connect(
			this.view.map, 'mapClicked',
			this, 'mapClicked');
		Obj.connect(
			this.view.filterList, 'filterClicked',
			this, 'filterClicked');
		Obj.connect(
			this.view.filterList, 'filterScopeClicked',
			this, 'filterScopeClicked');
		Obj.connect(
			this.view.filterList, 'filterEnabledClicked',
			this, 'filterEnabledClicked');
		Obj.connect(
			this.view.filterList, 'filterLabelRenameCommitted',
			this, 'filterLabelRenameCommitted');
	}

	// Load the target filter(s) into the editable draw layer. Returns how many
	// geometry-bearing features were added — 0 means there's nothing to edit, and
	// the caller must NOT enter the edit state (see startEditingFilterShape).
	private async addEditableFiltersToMap(filterId: number | Array<number>): Promise<number> {
		const ids = Array.isArray(filterId) ?
			filterId :
			[filterId];
		const filters = await this.fetchFilters();
		// The editable polygon may be NESTED (a leaf-scope geometry leaf under an
		// Include/Group sentinel), so search the WHOLE tree — fetchFilters() returns
		// top-level filters only. Without this a nested polygon's edit added no draw
		// feature yet still flipped shapeEditingFilterId, jamming every later click on
		// the shape (single + double both no-op via the currentShapeEditingFilterId
		// guards). Only geometry-bearing nodes are editable.
		const editing = this.flattenedFilterTree(filters).filter(
			f => (ids.indexOf(f.id) >= 0) && Boolean(f.geometry));
		// Static fill = the whole tree minus the leaves being edited (recursively), so
		// the editable copy in the draw layer isn't drawn twice.
		this.setMapFilterSourceData(this.filtersExcludingIds(filters, ids));
		this.view.map.addFeature(filtersToFeatureCollection(editing));
		return editing.length;
	}

	// Rebuild a filter tree with the given ids removed at any depth (so a nested
	// leaf being shape-edited isn't double-rendered by the static fill).
	private filtersExcludingIds(filters: Array<IFilter>, ids: Array<number>): Array<IFilter> {
		const rv: Array<IFilter> = [];
		for (const f of filters) {
			if (ids.indexOf(f.id) >= 0) {
				continue;
			}
			rv.push({...f, children: this.filtersExcludingIds(f.children || [], ids)});
		}
		return rv;
	}

	@SIGNAL
	private beginShapeEditing(filterId: number): void {
	}

	@SLOT
	closeFilterBox(): void {
		this.destroyFilterBox();
	}

	private async createFilter(data: Partial<INewFilter>): Promise<void> {
		const ident = generateUniqueIdent(this.identFilterPkMap);
		const cmd = new CreateFilterCommand(
			ident,
			data,
			this._undoCmdCreateFilterForwardFunc,
			this._undoCmdCreateFilterBackwardFunc);
		this.view.undoStack.push(cmd);
	}

	private async _createFilter(data: Partial<INewFilter>): Promise<IFilter> {
		const rv = await svc.filter.create(this.newFilterObject(data));
		this.filterCreated(rv, {parcelFilterId: rv.parentId || rv.id});
		return rv;
	}

	currentFilterBoxFilterId(): number {
		return this.filterBox ?
			this.filterBox.id() :
			Number.NaN;
	}

	currentlyEditingShape(): boolean {
		return isNumber(this.shapeEditingFilterId);
	}

	currentShapeEditingFilterId(): number {
		return isNumber(this.shapeEditingFilterId) ?
			this.shapeEditingFilterId :
			Number.NaN;
	}

	private async deleteFilter(filterId: number, backwardData: Partial<INewFilter>): Promise<void> {
		let ident: number | undefined = this._uniqueIdentForFilterPk(filterId);
		if (ident === undefined) {
			ident = generateUniqueIdent(this.identFilterPkMap);
			this.identFilterPkMap.set(ident, filterId);
		}
		const cmd = new DeleteFilterCommand(
			ident,
			backwardData,
			this._undoCmdDeleteFilterBackwardFunc,
			this._undoCmdDeleteFilterForwardFunc);
		this.view.undoStack.push(cmd);
	}

	private async _deleteFilter(pk: number): Promise<void> {
		let filter: IFilter | null = null;
		try {
			filter = await this.fetchFilter(pk);
		} catch (exc) {
			filter = null;
		}
		await svc.filter.delete(pk);
		this.filterDeleted(pk, {parcelFilterId: filter ? (filter.parentId || filter.id) : pk});
	}

	private deleteMapFeatures(featureIds: Array<number | string>): void {
		this.view.map.removeFeature(featureIds);
	}

	destroy(): void {
		Obj.disconnect(
			this.view.map, 'loaded',
			this, 'mapLoaded');
		window.removeEventListener('lb:project-filter-created', this.projectFilterCreatedEvent);
		this.destroyPolygonMenu();
		this.movingToFilter = null;
		this.shapeEditingFilterId = null;
		this.identFilterPkMap.clear();
		super.destroy();
	}

	private destroyFilterBox(): void {
		if (this.filterBox) {
			Obj.disconnect(
				this.filterBox, 'enabledChanged',
				this, 'filterBoxEnabledChanged');
			Obj.disconnect(
				this.filterBox, 'closeButtonClicked',
				this, 'filterBoxCloseButtonClicked');
			Obj.disconnect(
				this.filterBox, 'deleteButtonPressed',
				this, 'filterBoxDeleteButtonClicked');
			Obj.disconnect(
				this.filterBox, 'saveButtonPressed',
				this, 'filterBoxSaveButtonClicked');
			Obj.disconnect(
				this.filterBox, 'addButtonClicked',
				this, 'filterBoxAddButtonClicked');
			Obj.disconnect(
				this.filterBox, 'expressionChanged',
				this, 'filterBoxExpressionChanged');
			this.filterBox.destroy();
		}
		this.filterBox = null;
		this.filterBoxInline = false;
	}

	@SIGNAL
	private endShapeEditing(filterId: number): void {
	}

	async fetchFilter(pk: number): Promise<IFilter> {
		return await svc.filter.get(pk);
	}

	private async fetchFilters(opt?: Partial<Omit<IFilterListOpt, 'projectSlug'>>): Promise<Array<IFilter>> {
		return await svc.filter.list({
			...(opt || {}),
			projectSlug: this.view.slug,
		});
	}

	private async fetchListFilter(pk: number): Promise<IFilter | null> {
		const filters = await this.fetchFilters();
		for (const filter of filters) {
			if (filter.id === pk) {
				return filter;
			}
		}
		return null;
	}

	// Resolve a filter by id from the WHOLE project tree (top-level + nested
	// leaves), not just the top-level chip list. A two-zone Apply nests
	// geometry/county/selection leaves under a group sentinel, so a clicked
	// boundary's feature id (properties.filterId) is frequently a NESTED leaf
	// that fetchListFilter (top-level only) misses. The old fallback hit the
	// single-filter endpoint (/api/filters/<id>/) for such an id, which 404s
	// (it serves only project-scoped top-level rows) and, unguarded, surfaced
	// an "Uncaught (in promise)" 404 in the console. Search the flattened tree
	// first; only hit the network as a last resort, and swallow its 404 so a
	// stale/nested id resolves to a quiet null instead of console noise.
	private async fetchTreeFilter(pk: number): Promise<IFilter | null> {
		const found = this.flattenedFilterTree(await this.fetchFilters()).find(f => (f.id === pk));
		if (found) {
			return found;
		}
		try {
			return await this.fetchFilter(pk);
		} catch (exc) {
			logger.warning('fetchTreeFilter: filter %s not resolvable from the project tree or the filter endpoint.', pk, exc);
			return null;
		}
	}

	expressionTokenForUnifiedEditor(): IExpressionToken {
		return this.expressionToken;
	}

	async saveUnifiedFilterEditorDraft(filterId: number, draft: IFilterEditorDraft): Promise<IFilter> {
		await this.initPromise;
		const updated = await svc.filter.replaceTree(filterId, draft);
		this.setFilterListFilters([updated]);
		this.view.updateUnifiedFilterEditor(updated);
		this.filterUpdated(updated, {
			filterSourceChanged: false,
			parcelFilterId: filterId,
		});
		return updated;
	}

	// Lane E PR 1e-ii — the two-zone editor saves the WHOLE project tree via the
	// filter-tree IR endpoint (saveFilterTree), which rewrites every top-level
	// filter. After that write, reload the chip strip / map source / capabilities
	// from scratch (the per-filter delta paths above don't apply to a full rewrite).
	async reloadFiltersAfterTreeSave(): Promise<void> {
		await this.initPromise;
		const filters = await this.fetchFilters();
		const freshTopLevelIds = new Set(
			filters.filter(f => !isNumber(f.parentId)).map(f => f.id));
		const staleIds = this.view.filterList.filterIds().filter(id => !freshTopLevelIds.has(id));
		if (staleIds.length) {
			this.removeFilters(staleIds);
		}
		this.setFilterListFilters(filters);
		this.setMapFilterSourceData(filters);
		await this.refreshExpressionCapabilities();
	}

	private linkedPlaceUndoData(filter: IFilter): Partial<INewFilter> | null {
		if (!filter.linkedPlacePk) {
			return null;
		}
		return {
			enabled: filter.enabled,
			expression: filter.expression ? {
				lhs: filter.expression.lhs,
				operator: filter.expression.operator,
				rhs: filter.expression.rhs,
			} : null,
			geometry: null,
			geometryIsCircle: false,
			icon: filter.icon,
			label: filter.label,
			parentId: filter.parentId,
			placePk: filter.linkedPlacePk,
			projectSlug: this.view.slug,
		};
	}

	@SLOT
	private async filterBoxAddButtonClicked(): Promise<void> {
		if (this.filterBox) {
			await this.createFilter({
				enabled: true,
				expression: {
					lhs: '',
					operator: '',
					rhs: '',
				},
				geometry: null,
				geometryIsCircle: false,
				icon: '',
				label: '',
				parentId: this.filterBox.id(),
				projectSlug: this.view.slug,
			});
		}
	}

	@SLOT
	private async filterBoxCloseButtonClicked(): Promise<void> {
		const wasInline = this.filterBoxInline;
		this.destroyFilterBox();
		if (wasInline) {
			this.view.closeUnifiedWorkspacePanel();
		}
	}

	@SLOT
	private async filterBoxDeleteButtonClicked(filterId: number): Promise<void> {
		await this.deleteFilterFromEditor(filterId);
	}

	@SLOT
	private filterBoxEditButtonClicked(): void {
		if (this.filterBox) {
			if (isNumber(this.shapeEditingFilterId) && (this.shapeEditingFilterId === this.filterBox.id())) {
				this.stopEditingFilterShape();
			} else {
				this.startEditingFilterShape(this.filterBox.id());
			}
		}
	}

	@SLOT
	private async filterBoxEnabledChanged(filterId: number, enabled: boolean): Promise<void> {
		await this.updateFilter({pk: filterId, data: {enabled}});
	}

	@SLOT
	private async filterBoxExpressionChanged(filterId: number): Promise<void> {
		if (this.filterBox) {
			const childFilter = this.filterBox.childFilter(filterId);
			// const exprData = this.filterBox.expressionData(filterId);
			if (childFilter) {
				// await this.updateExpressions(filterId, [exprData]);
				await this.updateFilter({pk: filterId, data: childFilter});
			}
		}
	}

	@SLOT
	private async filterBoxSaveButtonClicked(filterId: number): Promise<void> {
		if (this.filterBox) {
			if (isNumber(filterId)) {
				if (filterId === this.filterBox.id()) {
					// Save parent filter
					// const obj = await this.fetchFilter(filterId);
					await this.updateFilter({pk: filterId, data: {label: this.filterBox.label()}});
					// if (this.currentlyEditingShape() && isNumber(this.shapeEditingFilterId) && (this.shapeEditingFilterId === obj.id)) {
					// 	await this.stopEditingFilterShape();
					// }
				} else {
					// Save child filter
					const childFilter = this.filterBox.childFilter(filterId);
					if (childFilter) {
						await this.updateFilter({pk: filterId, data: childFilter});
					} else {
						logger.error('No child filter returned for filter ID %s', filterId);
					}
					// const existingFilter = await this.fetchFilter(filterId);
					// const newExprData = this.filterBox.expressionData(filterId);
					// const exprId = existingFilter.expression ?
					// 	existingFilter.expression.id :
					// 	Number.NaN;
					// if (isNumber(exprId)) {
					// 	const exprData = this.filterBox.expressionData(filterId);
					// 	if (exprData) {
					// 		const existingExpr = existingFilter.expression || {};
					// 		const newExpr = {
					// 			...existingExpr,
					// 			...exprData,
					// 		};
					// 		await this.updateExpressions(existingFilter.id, [newExpr]);
					// 	} else {
					// 		logger.warning('filterSaveClicked: Given filter for update has related expression but object returns no such data.');
					// 	}
					// } else {
					// 	logger.warning('filterSaveClicked: Given filter for update has not related expression.');
					// }
				}
			} else {
				logger.error('filterBoxSaveButtonClicked: Invalid argument value.');
			}
		}
	}

	@SLOT
	private async filterClicked(filterId: number, point: Point): Promise<void> {
		const filter = await this.fetchTreeFilter(filterId);
		if (!filter) {
			logger.warning('filterClicked: filter %s not found in the project tree.', filterId);
			return;
		}
		await this.openUnifiedFilterEditor(filter);
	}

	@SLOT
	private async filterScopeClicked(filterId: number): Promise<void> {
		const filter = await this.fetchTreeFilter(filterId);
		if (!filter) {
			logger.warning('filterScopeClicked: filter %s not found in the project tree.', filterId);
			return;
		}
		this.flyToFilter(filter);
	}

	@SLOT
	private async filterLabelRenameCommitted(filterId: number, label: string): Promise<void> {
		await this.renameFilter(filterId, label);
	}

	@SLOT
	private async filterEnabledClicked(filterId: number): Promise<void> {
		const obj = await this.fetchTreeFilter(filterId);
		if (!obj) {
			logger.warning('filterEnabledClicked: filter %s not found in the project tree.', filterId);
			return;
		}
		if (this.filterUsesUnavailableField(obj)) {
			if (obj.enabled) {
				await this.toggleFilterEnabled(filterId, false, obj.enabled);
			}
			return;
		}
		await this.toggleFilterEnabled(filterId, !obj.enabled, obj.enabled);
	}

	@SIGNAL
	private filterCreated(filter: IFilter, opt?: {parcelFilterId?: number;}): void {
	}

	@SLOT
	private async _filtersCreated(filter: IFilter): Promise<void> {
		if (this.filterBox) {
			const parentFilterId = this.currentFilterBoxFilterId();
			if (isNumber(filter.parentId) && (filter.parentId === parentFilterId) && filter.expression && isNumber(filter.expression.id)) {
				this.filterBox.addChildFilterExpression(filter);
			}
		}
		const allFilters = await this.fetchFilters();
		this.setFilterListFilters(allFilters);
		this.setMapFilterSourceData(allFilters);
		await this.refreshExpressionCapabilities();
	}

	@SIGNAL
	private filterDeleted(filterId: number, opt?: {parcelFilterId?: number;}): void {
	}

	@SLOT
	private async _filterDeleted(filterId: number): Promise<void> {
		this.removeFilters([filterId]);
		const filters = await this.fetchFilters();
		this.setFilterListFilters(filters);
		this.setMapFilterSourceData(filters);
		this.view.clearUnifiedFilterEditorIfFilter(filterId);
		await this.refreshExpressionCapabilities();
	}

	@SIGNAL
	private filterUpdated(filter: IFilter, opt?: {filterSourceChanged?: boolean; parcelFilterId?: number;}): void {
	}

	@SLOT
	private async _filterUpdated(filter: IFilter, opt?: {filterSourceChanged?: boolean; parcelFilterId?: number;}): Promise<void> {
		if (this.filterBox) {
			const currFilterId = this.filterBox.id();
			if (filter.id === currFilterId) {
				await this.setFilterBoxData(filter);
			} else if (filter.parentId === currFilterId) {
				this.filterBox.setChildFilterData(filter.id, filter);
			}
		}
		this.setFilterListFilters([filter]);
		if (isNumber(filter.parentId)) {
			const parentFilter = await this.fetchListFilter(filter.parentId);
			if (parentFilter) {
				this.setFilterListFilters([parentFilter]);
				this.view.updateUnifiedFilterEditor(parentFilter);
			}
		}
		if (!isNumber(filter.parentId)) {
			this.view.updateUnifiedFilterEditor(filter);
		}
		if (!opt || opt.filterSourceChanged !== false) {
			this.setMapFilterSourceData(await this.fetchFilters());
		}
		await this.refreshExpressionCapabilities();
	}

	flyToFilter(filter: IFilter): void {
		if (filter.geometry && filter.geometry.bbox) {
			if (this.filterBox && (this.filterBox.id() !== filter.id)) {
				this.destroyFilterBox();
			}
			this.movingToFilter = filter;
			this.view.flyTo(<[number, number, number, number]>filter.geometry.bbox);
			return;
		}
		// Phase 3 follow-up to PR #73: address-search filters never
		// carry `filter.geometry` (they're parcel-uid lists, not
		// polygons), but when one resolves to exactly one parcel we
		// can fly to that parcel's bbox. Detection is structural — see
		// `singleParcelUidFromAddressFilter` — so we don't need any
		// extra metadata on the filter object.
		//
		// Highlight overlay (PR #73's 3s outline) is deliberately
		// skipped here: the project's filter-line layer already
		// outlines this parcel as part of the filter's parcel-set, so
		// the user lands on already-outlined geometry. Adding a second
		// orange overlay would compete with the permanent outline.
		const parcelUid = singleParcelUidFromAddressFilter(filter);
		if (parcelUid) {
			if (this.filterBox && (this.filterBox.id() !== filter.id)) {
				this.destroyFilterBox();
			}
			this.movingToFilter = filter;
			void this.flyToParcelBbox(parcelUid);
			return;
		}
		logger.warning('flyToFilter: Filter has no geometry and/or bbox.');
	}

	private async flyToParcelBbox(parcelUid: string): Promise<void> {
		try {
			const parcel = await svc.parcel.get(parcelUid);
			const bbox = parcel && parcel.areaBbox;
			if (bbox && bbox.length === 4) {
				this.view.flyTo(<[number, number, number, number]>bbox);
				return;
			}
			logger.warning('flyToParcelBbox: parcel %s has no areaBbox', parcelUid);
		} catch (exc) {
			logger.warning('flyToParcelBbox: failed to resolve parcel %s', parcelUid, exc);
		}
	}

	private async init(): Promise<void> {
		Obj.connect(
			this.view.map, 'loaded',
			this, 'mapLoaded');
		window.addEventListener('lb:project-filter-created', this.projectFilterCreatedEvent);
		this.addConnections();
		this.expressionToken = (await svc.ui.get()).expressions;
		await this.refreshExpressionCapabilities();
	}

	private filterCapabilityWarningsForCurrentSelection(excludeFields?: Set<string>): Array<IFilterCapabilityWarning> {
		const warnings: Array<IFilterCapabilityWarning> = [];
		for (const attr of this.expressionToken.attributes) {
			if (excludeFields && excludeFields.has(attr.value)) {
				continue;
			}
			const capability = this.expressionCapabilities[attr.value];
			if (!capability || !capability.disabled) {
				continue;
			}
			warnings.push({
				title: `${attr.label} unavailable`,
				message: capability.reason || 'Unavailable for the current selection.',
			});
		}
		return warnings;
	}

	private filterCapabilityWarningsForFilterData(filterData: IFilter): Array<IFilterCapabilityWarning> {
		const disabledFields = new Set<string>();
		const candidateFilters = [filterData, ...(filterData.children || [])];
		for (const filter of candidateFilters) {
			const lhs = filter.expression ? filter.expression.lhs : '';
			if (!lhs || disabledFields.has(lhs)) {
				continue;
			}
			const capability = this.expressionCapabilities[lhs];
			if (!capability || !capability.disabled) {
				continue;
			}
			disabledFields.add(lhs);
		}
		const warnings = this.filterCapabilityWarningsForCurrentSelection(disabledFields);
		for (const lhs of disabledFields) {
			const capability = this.expressionCapabilities[lhs];
			const attr = this.expressionToken.attributes.find(obj => obj.value === lhs);
			const label = attr ? attr.label : lhs;
			warnings.push({
				title: `${label} filter needs review`,
				message: (capability && capability.reason) || `${label} is unavailable for the current selection, but an existing filter is still configured.`,
			});
		}
		return warnings;
	}

	private fieldIsUnavailable(fieldValue: string): boolean {
		const capability = this.expressionCapabilities[fieldValue];
		return Boolean(capability && capability.disabled);
	}

	private filterUsesUnavailableField(filter: IFilter): boolean {
		const lhs = filter.expression ? filter.expression.lhs : '';
		return Boolean(lhs) && this.fieldIsUnavailable(lhs);
	}

	private flattenedFilterTree(filters: Array<IFilter>): Array<IFilter> {
		const rv: Array<IFilter> = [];
		const visit = (filter: IFilter): void => {
			rv.push(filter);
			for (const child of (filter.children || [])) {
				visit(child);
			}
		};
		for (const filter of filters) {
			visit(filter);
		}
		return rv;
	}

	isDisabled(): boolean {
		return this.disabled;
	}

	isFilterBoxOpen(): boolean {
		return Boolean(this.filterBox);
	}

	@SLOT
	private mapClicked(point: Point, coord: GeoCoordinate): void {
		switch (this.view.map.interactiveControlMode()) {
			case InteractiveMapControlMode.ShapeSelect:
			case InteractiveMapControlMode.DrawShape:
			case InteractiveMapControlMode.InfoMode:
				return;
		}
		// Lane E — resolve the clicked polygon from the RENDERED feature (works for
		// nested leaves; fetchFilters(point) was top-level-only), and disambiguate
		// single-click (open the editor) from double-click (edit the shape) with a
		// short debounce. A second click within the window cancels the pending open
		// and edits instead; otherwise the panel always opened on the first click and
		// the double-click never landed.
		const filterId = this.view.map.filterIdAtPoint(point.x(), point.y());
		if (this.pendingMapClickTimer !== null) {
			window.clearTimeout(this.pendingMapClickTimer);
			this.pendingMapClickTimer = null;
			if (filterId !== null && this.currentShapeEditingFilterId() !== filterId) {
				this.view.closeUnifiedWorkspacePanel();
				void this.beginEditingFilterShape(filterId);
			}
			return;
		}
		this.pendingMapClickTimer = window.setTimeout(() => {
			this.pendingMapClickTimer = null;
			this.handleSingleMapClick(filterId, point);
		}, 280);
	}

	private handleSingleMapClick(filterId: number | null, point: Point): void {
		if (filterId === null) {
			// No filter polygon under the click — close any stale popups.
			this.destroyFilterBox();
			this.destroyPolygonMenu();
			return;
		}
		if (this.currentShapeEditingFilterId() === filterId) {
			// Clicking the shape currently being edited — leave the user editing.
			return;
		}
		// Single click on a polygon anchors a per-polygon action popup at the click
		// point (Edit shape / Hide-Show / Delete / open the editor) instead of jumping
		// straight into the panel — so a NESTED polygon (which has no chip after a
		// two-zone Apply, setFilterListFilters being top-level only) is still directly
		// manipulable on the map. Double-click still shortcuts to Edit shape.
		void this.openPolygonMenu(filterId, point);
	}

	// Lane E — the per-polygon map popup. Operates on the persisted filter row by id
	// (resolved from the rendered feature via filterIdAtPoint), so it works for the
	// nested leaves a two-zone Apply produces, not just top-level chips. Each action
	// reuses an existing, audited path: beginEditingFilterShape (vertex edit),
	// filterEnabledClicked (toggle enabled — patches the row, nested rows included),
	// deleteFilterFromEditor (delete + undo), openProjectFilterEditor (the panel).
	private async openPolygonMenu(filterId: number, point: Point): Promise<void> {
		const obj = await this.fetchTreeFilter(filterId);
		// A double-click during the fetch may have started shape-editing — don't pop.
		if (this.currentShapeEditingFilterId() === filterId) {
			return;
		}
		const enabled = !obj || obj.enabled !== false;
		this.destroyPolygonMenu();
		const menu = new Menu();
		const items: Array<{label: string; action: () => void;}> = [
			{
				label: 'Edit shape',
				action: () => {
					this.view.closeUnifiedWorkspacePanel();
					void this.beginEditingFilterShape(filterId);
				},
			},
			{
				label: enabled ? 'Hide on map' : 'Show on map',
				action: () => { void this.filterEnabledClicked(filterId); },
			},
			{
				// Open the editor focused on the GROUP that scopes this boundary, so
				// the user lands on the right group to add criteria to (Stage 2).
				label: 'Edit filter group…',
				action: () => { this.view.openProjectFilterEditorForLeaf(filterId); },
			},
			{
				label: 'Delete',
				action: () => { void this.deleteFilterFromEditor(filterId); },
			},
		];
		this.polygonMenuActions = items.map(it => it.action);
		for (const it of items) {
			menu.addItem(it.label);
		}
		Obj.connect(menu, 'selectionChanged', this, 'polygonMenuSelectionChanged');
		Obj.connect(menu, 'closed', this, 'polygonMenuClosed');
		this.polygonMenu = menu;
		// event.point is map-canvas-relative; the Menu positions in viewport px.
		const vp = this.view.map.viewportPointFromMapPoint(point.x(), point.y());
		menu.open(new Point(vp.x, vp.y));
	}

	@SLOT
	private polygonMenuSelectionChanged(index: number): void {
		const action = this.polygonMenuActions[index];
		if (action) {
			action();
		}
	}

	@SLOT
	private polygonMenuClosed(): void {
		this.destroyPolygonMenu();
	}

	private destroyPolygonMenu(): void {
		if (this.polygonMenu) {
			Obj.disconnect(this.polygonMenu, 'selectionChanged', this, 'polygonMenuSelectionChanged');
			Obj.disconnect(this.polygonMenu, 'closed', this, 'polygonMenuClosed');
			this.polygonMenu.destroy();
		}
		this.polygonMenu = null;
		this.polygonMenuActions = [];
	}

	@SLOT
	private async mapFeaturesCreated<G extends GeoJsonGeometryNoCollection = GeoJsonGeometryNoCollection, P = GeoJsonProperties>(features: Array<GeoJsonFeature<G, P>>): Promise<void> {
		const tempFeatIds: Array<string> = [];
		for (let i = 0; i < features.length; ++i) {
			const feat = features[i];
			if (typeof feat.id === 'string') {
				tempFeatIds.push(feat.id);
			}
		}
		const prepped = preparedFeatures(features);
		for (let i = 0; i < prepped.length; ++i) {
			const feat = prepped[i];
			await this.createFilter({geometry: feat.geometry, geometryIsCircle: feat.properties.circle});
		}
		this.deleteMapFeatures(tempFeatIds);
	}

	@SLOT
	private async mapFeaturesDeleted<G extends GeoJsonGeometryNoCollection = GeoJsonGeometryNoCollection, P = GeoJsonProperties>(features: Array<GeoJsonFeature<G, P>>): Promise<void> {
		for (let i = 0; i < features.length; ++i) {
			const feat = features[i];
			if (feat.id !== undefined) {
				const featId = isNumber(feat.id) ?
					feat.id :
					Number(feat.id);
				if (isNumber(featId)) {
					const listFilter = await this.fetchListFilter(featId);
					const backwardData = (listFilter && this.linkedPlaceUndoData(listFilter)) || await this.fetchFilter(featId);
					await this.deleteFilter(featId, backwardData);
				}
			}
		}
	}

	@SLOT
	private async mapFeaturesUpdated<G extends GeoJsonGeometryNoCollection = GeoJsonGeometryNoCollection, P = GeoJsonProperties>(features: Array<GeoJsonFeature<G, P>>): Promise<void> {
		const prepped = preparedUpdateFeatures(features);
		for (let i = 0; i < prepped.length; ++i) {
			const feat = prepped[i];
			await this.updateFilter({
				pk: feat.id,
				data: {
					geometry: feat.geometry,
					geometryIsCircle: feat.properties.circle,
				},
			});
		}
	}

	@SLOT
	private async mapLoaded(): Promise<void> {
		const filters = await this.fetchFilters();
		this.setMapFilterSourceData(filters);
		this.setFilterListFilters(filters);
	}

	async syncFilterList(): Promise<void> {
		this.setFilterListFilters(await this.fetchFilters());
	}

	// Lane E PR 1e-iv — public entry to the (still-intact) shape-editing machinery,
	// re-surfaced for the two-zone editor's geometry leaf rows after 1e-ii dropped
	// the FilterBox "Edit Shape" affordance. Loads the polygon into the map's
	// editable draw layer (vertex editing); the geometry saves on commit, v1 or v2.
	async beginEditingFilterShape(filterId: number): Promise<void> {
		if (!isNumber(filterId) || filterId <= 0) {
			return;
		}
		await this.startEditingFilterShape(filterId);
	}

	async deleteFilterFromEditor(filterId: number): Promise<void> {
		if (isNumber(filterId) && (filterId > 0)) {
			const listFilter = await this.fetchListFilter(filterId);
			const backwardData = (listFilter && this.linkedPlaceUndoData(listFilter)) || await this.fetchFilter(filterId);
			await this.deleteFilter(filterId, backwardData);
		}
		if (this.filterBox) {
			const wasInline = this.filterBoxInline;
			if (this.filterBox.isTopLevelFilter(filterId)) {
				this.destroyFilterBox();
				if (wasInline) {
					this.view.closeUnifiedWorkspacePanel();
				}
			} else {
				this.filterBox.removeChildFilter(filterId);
			}
		}
	}

	async renameFilter(filterId: number, label: string): Promise<void> {
		const next = label.trim();
		if (!next || !isNumber(filterId)) {
			return;
		}
		await this.updateFilter({pk: filterId, data: {label: next}});
	}

	async saveCurrentFilterBox(): Promise<void> {
		const filterId = this.currentFilterBoxFilterId();
		if (isNumber(filterId)) {
			await this.filterBoxSaveButtonClicked(filterId);
		}
	}

	attachCurrentFilterBoxToUnifiedHost(): void {
		if (!this.filterBox) {
			return;
		}
		const host = this.view.unifiedFilterEditorHost();
		const filterBoxEl = this.filterBox.element();
		if (host && filterBoxEl) {
			if (filterBoxEl.parentElement !== host) {
				host.appendChild(filterBoxEl);
			}
			this.filterBoxInline = true;
			this.filterBox.setClass(true, 'lb-filterbox--unified-editor');
			this.filterBox.removeClass(ElObj.cssClassNames.VisibilityHidden);
		}
	}

	@SLOT
	private mapMoveEnded(): void {
		const filter = this.movingToFilter;
		this.movingToFilter = null;
		if (filter) {
			// The redesign keeps fly-to behavior but no longer opens the old
			// map-anchored FilterBox after movement completes.
			this.destroyFilterBox();
		}
	}

	private newFilterObject(data?: Partial<INewFilter>): INewFilter {
		return {
			...baseFilterObject(data),
			expression: data ?
				(data.expression || null) :
				null,
			projectSlug: this.view.slug,
		};
	}

	private async openUnifiedFilterEditor(filterData: IFilter): Promise<void> {
		this.closeFilterBox();
		this.view.openUnifiedFilterEditor(filterData);
	}

	private async openFilterBox(filterData: IFilter, pos: Point, opt?: {inlineHost?: HTMLElement | null;}): Promise<void> {
		await this.initPromise;
		const inlineHost = opt ? (opt.inlineHost || null) : null;
		if (!this.filterBox) {
			this.filterBox = new FilterBox({
				capabilityWarnings: this.filterCapabilityWarnings,
				expressionToken: this.expressionToken,
			});
			this.filterBox.addClass(ElObj.cssClassNames.VisibilityHidden);
			Obj.connect(
				this.filterBox, 'closeButtonClicked',
				this, 'filterBoxCloseButtonClicked');
			Obj.connect(
				this.filterBox, 'deleteButtonPressed',
				this, 'filterBoxDeleteButtonClicked');
			Obj.connect(
				this.filterBox, 'saveButtonPressed',
				this, 'filterBoxSaveButtonClicked');
			Obj.connect(
				this.filterBox, 'addButtonClicked',
				this, 'filterBoxAddButtonClicked');
			Obj.connect(
				this.filterBox, 'editButtonClicked',
				this, 'filterBoxEditButtonClicked');
			Obj.connect(
				this.filterBox, 'enabledChanged',
				this, 'filterBoxEnabledChanged');
			Obj.connect(
				this.filterBox, 'expressionChanged',
				this, 'filterBoxExpressionChanged');
			const filterBoxEl = this.filterBox.element();
			if (inlineHost && filterBoxEl) {
				inlineHost.appendChild(filterBoxEl);
			} else {
				ElObj.body().appendChild(this.filterBox);
			}
		} else {
			this.filterBox.setExpressionToken(this.expressionToken);
			const filterBoxEl = this.filterBox.element();
			if (filterBoxEl) {
				if (inlineHost) {
					inlineHost.appendChild(filterBoxEl);
				} else if (filterBoxEl.parentElement !== document.body) {
					document.body.appendChild(filterBoxEl);
				}
			}
		}
		this.filterBoxInline = Boolean(inlineHost);
		this.filterBox.setClass(this.filterBoxInline, 'lb-filterbox--unified-editor');
		await this.setFilterBoxData(filterData);
		if (!this.filterBoxInline) {
			this.setFilterBoxPosition(pos);
		}
		this.filterBox.removeClass(ElObj.cssClassNames.VisibilityHidden);
	}

	private async refreshExpressionCapabilities(): Promise<void> {
		const refreshSeq = ++this.filterCapabilityRefreshSeq;
		try {
			const payload = await svc.project.filterCapabilities(this.view.slug);
			if (refreshSeq !== this.filterCapabilityRefreshSeq) {
				return;
			}
			this.expressionCapabilities = payload.fields || {};
			if (payload.expressionToken) {
				this.expressionToken = payload.expressionToken;
			}
		} catch (exc) {
			if (refreshSeq !== this.filterCapabilityRefreshSeq) {
				return;
			}
			logger.warning('refreshExpressionCapabilities: Failed to fetch filter capabilities.');
			this.expressionCapabilities = {};
		}
		this.filterCapabilityWarnings = this.filterCapabilityWarningsForCurrentSelection();
		const filters = this.flattenedFilterTree(await this.fetchFilters());
		for (const filter of filters) {
			if (filter.enabled && this.filterUsesUnavailableField(filter)) {
				await this._updateFilter({
					...filter,
					children: filter.children || [],
					enabled: false,
				});
			}
		}
		if (this.filterBox) {
			this.filterBox.setExpressionToken(this.expressionToken);
			const filterId = this.currentFilterBoxFilterId();
			if (isNumber(filterId)) {
				try {
					const filterData = await this.fetchFilter(filterId);
					this.filterBox.setCapabilityWarnings(this.filterCapabilityWarningsForFilterData(filterData));
				} catch (exc) {
					logger.warning('refreshExpressionCapabilities: Failed to refresh open filter warning state.');
					this.filterBox.setCapabilityWarnings(this.filterCapabilityWarnings);
				}
			} else {
				this.filterBox.setCapabilityWarnings(this.filterCapabilityWarnings);
			}
		}
	}

	@SLOT
	private async placeAutoCompleteActivated(place: IPlace): Promise<void> {
		await this.createFilter({placePk: place.uid});
	}

	@bind
	private async projectFilterCreatedEvent(event: Event): Promise<void> {
		const detail = (event as CustomEvent).detail || {};
		const filters = Array.isArray(detail.filters) ? detail.filters : [];
		if (!filters.length) {
			const allFilters = await this.fetchFilters();
			this.setFilterListFilters(allFilters);
			this.setMapFilterSourceData(allFilters);
			await this.refreshExpressionCapabilities();
			return;
		}
		if (filters.length > 1) {
			this.filterCreated(filters[0]);
			return;
		}
		this.filterCreated(filters[0], {parcelFilterId: filters[0].parentId || filters[0].id});
	}

	private removeConnections(): void {
		Obj.disconnect(
			this, 'beginShapeEditing',
			this, 'closeFilterBox');
		Obj.disconnect(
			this, 'filterCreated',
			this, '_filtersCreated');
		Obj.disconnect(
			this, 'filterDeleted',
			this, '_filterDeleted');
		Obj.disconnect(
			this, 'filterUpdated',
			this, '_filterUpdated');
		Obj.disconnect(
			this.view, 'placeAutoCompleteActivated',
			this, 'placeAutoCompleteActivated');
		Obj.disconnect(
			this.view.map, 'moveEnded',
			this, 'mapMoveEnded');
		Obj.disconnect(
			this.view.map, 'featuresCreated',
			this, 'mapFeaturesCreated');
		Obj.disconnect(
			this.view.map, 'featuresDeleted',
			this, 'mapFeaturesDeleted');
		Obj.disconnect(
			this.view.map, 'featuresUpdated',
			this, 'mapFeaturesUpdated');
		Obj.disconnect(
			this.view.map, 'mapClicked',
			this, 'mapClicked');
		Obj.disconnect(
			this.view.filterList, 'filterClicked',
			this, 'filterClicked');
		Obj.disconnect(
			this.view.filterList, 'filterScopeClicked',
			this, 'filterScopeClicked');
		Obj.disconnect(
			this.view.filterList, 'filterEnabledClicked',
			this, 'filterEnabledClicked');
	}

	private async removeEditableFiltersFromMap(filterId: number | Array<number>): Promise<void> {
		const filters = await this.fetchFilters();
		this.view.map.setFilterSourceData(filtersToFeatureCollection(filters));
		this.view.map.removeFeature(filterId);
	}

	private removeFilters(filterIds: Array<number>): void {
		const currFilterBoxId = this.currentFilterBoxFilterId();
		if (filterIds.indexOf(currFilterBoxId) >= 0) {
			this.closeFilterBox();
		} else if (this.filterBox) {
			for (const obj of filterIds) {
				if (this.filterBox.hasChildFilter(obj)) {
					this.filterBox.removeChildFilter(obj);
				}
			}
		}
		this.removeFiltersFromFilterList(filterIds);
		this.removeFiltersFromMap(filterIds);
	}

	private removeFiltersFromFilterList(filterIds: Array<number>): void {
		for (const filterId of filterIds) {
			if (this.view.filterList.indexForFilterId(filterId) >= 0) {
				this.view.filterList.removeFilter(filterId);
			}
		}
		if (this.view.filterList.count() > 0) {
			this.view.setFilterListDividerVisible(true);
		} else {
			this.view.setFilterListDividerVisible(false);
		}
		this.view.syncFilterSummaryFromFilterList();
	}

	private removeFiltersFromMap(filterIds: Array<number>): void {
		this.view.map.removeFeature(filterIds);
	}

	setDisabled(disabled: boolean): void {
		if (disabled === this.disabled) {
			return;
		}
		this.disabled = disabled;
		this.filterBox && this.filterBox.setDisabled(this.disabled);
		if (this.disabled) {
			this.destroyFilterBox();
			if (this.currentlyEditingShape()) {
				this.stopEditingFilterShape();
			}
			this.removeConnections();
		} else {
			this.addConnections();
		}
	}

	private async setFilterBoxData(data: IFilter): Promise<void> {
		if (!this.filterBox) {
			logger.error('setFilterBoxData: FilterBox not instantiated');
			return;
		}
		this.filterBox.setCapabilityWarnings(this.filterCapabilityWarningsForFilterData(data));
		this.filterBox.setFilter(data);
		const nextChildIds = new Set<number>();
		for (const child of data.children) {
			if (isNumber(child.id) && child.expression) {
				nextChildIds.add(child.id);
			}
		}
		for (const childId of this.filterBox.childFilterIds()) {
			if (!nextChildIds.has(childId)) {
				this.filterBox.removeChildFilter(childId);
			}
		}
		for (const child of data.children) {
			// Currently: For all child filters, if they have a related
			// expression, they get added. Otherwise we've no real reason to
			// add them.
			if (child.expression) {
				this.filterBox.addChildFilterExpression(child);
			}
		}
	}

	private setFilterBoxPosition(x: number, y: number): void;
	private setFilterBoxPosition(point: Point): void;
	private setFilterBoxPosition(a: Point | number, b?: number): void {
		if (this.filterBox) {
			let x: number;
			let y: number;
			if (isNumber(a) && isNumber(b)) {
				x = a;
				y = b;
			} else {
				x = (<Point>a).x();
				y = (<Point>a).y();
			}
			this.filterBox.setPosition(x, y);
		}
	}

	private setFilterListFilters(filters: Array<IFilter>): void {
		for (const filter of filters) {
			if (this.view.filterList.indexForFilterId(filter.id) >= 0) {
				this.view.filterList.updateFilter(filter);
			} else if (!isNumber(filter.parentId)) {
				// Top-level filters only
				this.view.filterList.addFilter(filter);
			}
		}
		if (this.view.filterList.count() > 0) {
			this.view.setFilterListDividerVisible(true);
		} else {
			this.view.setFilterListDividerVisible(false);
		}
		this.view.syncFilterSummaryFromFilterList();
	}

	private setMapFilterSourceData(filters: Array<IFilter>): void {
		if (isNumber(this.shapeEditingFilterId)) {
			filters = filters.filter(f => (f.id !== this.shapeEditingFilterId));
		}
		this.view.map.setFilterSourceData(filtersToFeatureCollection(filters));
	}

	private async startEditingFilterShape(filterId: number): Promise<void> {
		if (filterId !== this.shapeEditingFilterId) {
			const added = await this.addEditableFiltersToMap(filterId);
			if (!added) {
				// Nothing editable for this filter (e.g. a non-geometry leaf, or it
				// vanished). Do NOT enter the edit state — a stuck shapeEditingFilterId
				// jams every later click on the shape. Restore the full static source.
				this.setMapFilterSourceData(await this.fetchFilters());
				logger.warning(`startEditingFilterShape: no editable geometry for filter ${filterId}`);
				return;
			}
			this.view.setSelectedMapFeatures(filterId);
			this.shapeEditingFilterId = filterId;
			this.beginShapeEditing(this.shapeEditingFilterId);
		}
	}

	async stopEditingFilterShape(): Promise<void> {
		if (isNumber(this.shapeEditingFilterId)) {
			await this.removeEditableFiltersFromMap(this.shapeEditingFilterId);
			this.endShapeEditing(this.shapeEditingFilterId);
		} else {
			logger.warning('stopEditingFilterShape: Called while no filter currently editing.');
		}
		this.shapeEditingFilterId = null;
	}

	// @bind
	// private async _undoCmdCreateFilterForwardFunc(uniqueIdent: number, data: Partial<INewFilter>): Promise<void> {
	// 	// Create new filter
	// 	const filters = await this._createFilter([data]);
	// 	assert(filters.length === 1);
	// 	this.identFilterPkMap.set(uniqueIdent, filters[0].id);
	// }

	@bind
	private async _undoCmdCreateFilterForwardFunc(ident: number, data: Partial<INewFilter>): Promise<void> {
		const filter = await this._createFilter(data);
		this.identFilterPkMap.set(ident, filter.id);
	}

	@bind
	private async _undoCmdCreateFilterBackwardFunc(ident: number): Promise<void> {
		const pk = this.identFilterPkMap.get(ident);
		if (pk === undefined) {
			// BIG ERROR
			logger.error('createBackward: Got undo command without mapped identifier');
		} else {
			await this._deleteFilter(pk);
		}
	}

	@bind
	private async _undoCmdDeleteFilterForwardFunc(ident: number): Promise<void> {
		const pk = this.identFilterPkMap.get(ident);
		if (pk === undefined) {
			// BIG ERROR
			logger.error('deleteForward: Got undo command without mapped identifier');
		} else {
			await this._deleteFilter(pk);
		}
	}

	@bind
	private async _undoCmdDeleteFilterBackwardFunc(ident: number, data: Partial<INewFilter>): Promise<void> {
		const pk = this.identFilterPkMap.get(ident);
		if (pk === undefined) {
			// BIG ERROR
			logger.error('deleteBackward: Got undo command without mapped identifier');
		} else {
			const filter = await this._createFilter(data);
			this.identFilterPkMap.set(ident, filter.id);
		}
	}

	@bind
	private async _undoCmdToggleFilterEnabledForwardFunc(ident: number, enabled: boolean): Promise<void> {
		const pk = this.identFilterPkMap.get(ident);
		if (pk === undefined) {
			logger.error('toggleForward: Got undo command without mapped identifier');
		} else {
			await this._patchFilterEnabled(pk, enabled);
		}
	}

	@bind
	private async _undoCmdToggleFilterEnabledBackwardFunc(ident: number, enabled: boolean): Promise<void> {
		const pk = this.identFilterPkMap.get(ident);
		if (pk === undefined) {
			logger.error('toggleBackward: Got undo command without mapped identifier');
		} else {
			await this._patchFilterEnabled(pk, enabled);
		}
	}

	@bind
	private async _undoCmdUpdateFilterForwardFunc(ident: number, data: IFilter): Promise<void> {
		const pk = this.identFilterPkMap.get(ident);
		if (pk === undefined) {
			// BIG ERROR
			logger.error('updateForward: Got undo command without mapped identifier');
		} else {
			data.id = pk;
			await this._updateFilter(data);
		}
	}

	@bind
	private async _undoCmdUpdateFilterBackwardFunc(ident: number, data: IFilter): Promise<void> {
		const pk = this.identFilterPkMap.get(ident);
		if (pk === undefined) {
			// BIG ERROR
			logger.error('updateBackward: Got undo command without mapped identifier');
		} else {
			data.id = pk;
			await this._updateFilter(data);
		}
	}

	private _uniqueIdentForFilterPk(pk: number): number | undefined {
		for (const [ident, filterPk] of this.identFilterPkMap) {
			if (filterPk === pk) {
				return ident;
			}
		}
		return undefined;
	}

	private async updateFilter(data: {pk: number; data: Partial<IFilter>}): Promise<void> {
		let ident = this._uniqueIdentForFilterPk(data.pk);
		if (ident === undefined) {
			ident = generateUniqueIdent(this.identFilterPkMap);
			this.identFilterPkMap.set(ident, data.pk);
		}
		const current = await this.fetchFilter(data.pk);
		const updated = {...current, ...data.data};
		const cmd = new UpdateFilterCommand(
			ident,
			updated,
			this._undoCmdUpdateFilterForwardFunc,
			current,
			this._undoCmdUpdateFilterBackwardFunc);
		this.view.undoStack.push(cmd);
	}

	async _updateFilter(data: IFilter): Promise<IFilter> {
		const rv = await svc.filter.update(data);
		this.filterUpdated(rv, {
			filterSourceChanged: rv.parentId === null,
			parcelFilterId: rv.parentId || rv.id,
		});
		return rv;
	}

	private async toggleFilterEnabled(filterId: number, enabled: boolean, currentEnabled: boolean): Promise<void> {
		let ident = this._uniqueIdentForFilterPk(filterId);
		if (ident === undefined) {
			ident = generateUniqueIdent(this.identFilterPkMap);
			this.identFilterPkMap.set(ident, filterId);
		}
		const cmd = new ToggleFilterEnabledCommand(
			ident,
			enabled,
			this._undoCmdToggleFilterEnabledForwardFunc,
			currentEnabled,
			this._undoCmdToggleFilterEnabledBackwardFunc,
		);
		this.view.undoStack.push(cmd);
	}

	async _patchFilterEnabled(pk: number, enabled: boolean): Promise<IFilter> {
		const rv = await svc.filter.patch(pk, {enabled});
		this.filterUpdated(rv, {
			filterSourceChanged: false,
			parcelFilterId: rv.parentId || rv.id,
		});
		return rv;
	}
}

interface ICreateFilterCmdInfo {
	data: Partial<INewFilter>;
	ident: number;
}

function filterToFeature(filter: IFilter): GeoJsonFilterFeaturePerhapsWithoutGeometry {
	let circle: boolean = false;
	let center: Array<number> | undefined = undefined;
	let geometry: GeoJsonPolygon | GeoJsonMultiPolygon | null = null;
	if (filter.geometry) {
		if (filter.geometryIsCircle) {
			circle = true;
			geometry = {
				coordinates: (filter.geometry.coordinates.length > 0) ?
					filter.geometry.coordinates[0] :
					[],
				type: 'Polygon',
			};
			center = geometry.coordinates.length > 0 ?
				geometryCenter(geometry).geometry.coordinates :
				[];
		} else {
			geometry = filter.geometry;
		}
	}
	return {
		id: filter.id,
		geometry,
		properties: {
			center,
			circle,
			label: filter.label,
			// Carried in properties because the source uses generateId:true (the
			// feature's own id is auto-generated, not the filter id).
			filterId: filter.id,
		},
		type: 'Feature',
	};
}

function ensureMultipolygon<P = GeoJsonProperties>(feature: GeoJsonFeature<GeoJsonPolygon | GeoJsonMultiPolygon, P>): GeoJsonFeature<GeoJsonMultiPolygon, P> {
	if (feature.geometry.type === 'MultiPolygon') {
		return <GeoJsonFeature<GeoJsonMultiPolygon, P>>feature;
	}
	return {
		...feature,
		geometry: {
			coordinates: [feature.geometry.coordinates],
			type: 'MultiPolygon',
		},
	};
}

function baseFilterObject(data?: Partial<IFilterBase>): IFilterBase {
	return {
		enabled: true,
		geometry: null,
		geometryIsCircle: false,
		icon: '',
		label: '',
		parentId: null,
		...(data || {}),
	};
}

function preparedFeatures<G extends GeoJsonGeometryNoCollection = GeoJsonGeometryNoCollection, P = GeoJsonProperties>(features: Array<GeoJsonFeature<G, P>>): Array<GeoJsonFeature<GeoJsonMultiPolygon, {circle: boolean;}>> {
	const rv: Array<GeoJsonFeature<GeoJsonMultiPolygon, {circle: boolean;}>> = [];
	for (let i = 0; i < features.length; ++i) {
		const feat = features[i];
		if (feat.id === undefined) {
			logger.warning('preparedFeatures: Got feature with no ID.');
			continue;
		}
		if (!feat.geometry) {
			logger.warning('preparedFeatures: Got feature with no geometry.');
			continue;
		}
		if (!((feat.geometry.type === 'Polygon') || (feat.geometry.type === 'MultiPolygon'))) {
			logger.warning('preparedFeatures: Got feature with invalid geometry type.');
			continue;
		}
		const circle: boolean = Boolean(feat.properties && (<{circle?: boolean;}>feat.properties).circle);
		const mpolyFeat = ensureMultipolygon(<GeoJsonFeature<GeoJsonPolygon | GeoJsonMultiPolygon>>feat);
		const properties = mpolyFeat.properties ?
			{...mpolyFeat.properties, circle} :
			{circle};
		rv.push({
			...mpolyFeat,
			properties,
		});
	}
	return rv;
}

function preparedUpdateFeatures<G extends GeoJsonGeometryNoCollection = GeoJsonGeometryNoCollection, P = GeoJsonProperties>(features: Array<GeoJsonFeature<G, P>>): Array<GeoJsonFeature<GeoJsonMultiPolygon, {circle: boolean;}> & {id: number}> {
	const rv: Array<GeoJsonFeature<GeoJsonMultiPolygon, {circle: boolean;}> & {id: number}> = [];
	const prepped = preparedFeatures(features);
	for (let i = 0; i < prepped.length; ++i) {
		const feat = prepped[i];
		const featId = isNumber(feat.id) ?
			feat.id :
			Number(feat.id);
		if (!isNumber(featId)) {
			logger.warning('preparedFeatures: Got feature with invalid ID/ID type.');
			continue;
		}
		feat.id = featId;
		rv.push(<GeoJsonFeature<GeoJsonMultiPolygon, {circle: boolean;}> & {id: number}>feat);
	}
	return rv;
}

function filtersToFeatureCollection(filters: Array<IFilter>): {features: Array<GeoJsonFilterFeature>; type: 'FeatureCollection'} {
	const features: Array<GeoJsonFilterFeature> = [];
	// Lane E PR 1e-ii — recurse into children. The two-zone editor's IR save nests
	// geometry leaves UNDER an include group sentinel (which has no geometry of its
	// own), so a top-level-only pass would draw nothing and a drawn polygon would
	// silently vanish from the map even though the resolver still applies it.
	const visit = (filter: IFilter) => {
		const feature = filterToFeature(filter);
		if (feature.geometry) {
			features.push(<GeoJsonFilterFeature>feature);
		}
		for (const child of filter.children || []) {
			visit(child);
		}
	};
	for (const filter of filters) {
		visit(filter);
	}
	return {
		features,
		type: 'FeatureCollection',
	};
}

class CreateFilterCommand extends UndoCommand {
	forwardData: Partial<INewFilter>;
	forwardFunc: (uniqueIdent: number, data: Partial<INewFilter>) => any;
	backwardFunc: (uniqueIdent: number) => any;
	uniqueIdent: number;

	constructor(uniqueIdent: number, forwardData: Partial<INewFilter>, forwardFunc: (uniqueIdent: number, data: Partial<INewFilter>) => any, backwardFunc: (uniqueIdent: number) => any) {
		super('create filter');
		this.backwardFunc = backwardFunc;
		this.forwardData = forwardData;
		this.forwardFunc = forwardFunc;
		this.uniqueIdent = uniqueIdent;
	}

	async redo(): Promise<void> {
		await this.forwardFunc(this.uniqueIdent, this.forwardData);
	}

	async undo(): Promise<void> {
		await this.backwardFunc(this.uniqueIdent);
	}
}

class DeleteFilterCommand extends UndoCommand {
	backwardData: Partial<INewFilter>;
	backwardFunc: (uniqueIdent: number, data: Partial<INewFilter>) => any;
	forwardFunc: (uniqueIdent: number) => any;
	uniqueIdent: number;

	constructor(uniqueIdent: number, backwardData: Partial<INewFilter>, backwardFunc: (uniqueIdent: number, data: Partial<INewFilter>) => any, forwardFunc: (uniqueIdent: number) => any) {
		super('delete filter');
		this.backwardData = backwardData;
		this.backwardFunc = backwardFunc;
		this.forwardFunc = forwardFunc;
		this.uniqueIdent = uniqueIdent;
	}

	async redo(): Promise<void> {
		await this.forwardFunc(this.uniqueIdent);
	}

	async undo(): Promise<void> {
		await this.backwardFunc(this.uniqueIdent, this.backwardData);
	}
}

class ToggleFilterEnabledCommand extends UndoCommand {
	uniqueIdent: number;
	forwardEnabled: boolean;
	backwardEnabled: boolean;
	forwardFunc: (uniqueIdent: number, enabled: boolean) => any;
	backwardFunc: (uniqueIdent: number, enabled: boolean) => any;

	constructor(uniqueIdent: number, forwardEnabled: boolean, forwardFunc: (uniqueIdent: number, enabled: boolean) => any, backwardEnabled: boolean, backwardFunc: (uniqueIdent: number, enabled: boolean) => any) {
		super('toggle filter');
		this.uniqueIdent = uniqueIdent;
		this.forwardEnabled = forwardEnabled;
		this.backwardEnabled = backwardEnabled;
		this.forwardFunc = forwardFunc;
		this.backwardFunc = backwardFunc;
	}

	async redo(): Promise<void> {
		await this.forwardFunc(this.uniqueIdent, this.forwardEnabled);
	}

	async undo(): Promise<void> {
		await this.backwardFunc(this.uniqueIdent, this.backwardEnabled);
	}
}

class UpdateFilterCommand extends UndoCommand {
	uniqueIdent: number;
	forwardData: IFilter;
	backwardData: IFilter;
	forwardFunc: (uniqueIdent: number, data: IFilter) => any;
	backwardFunc: (uniqueIdent: number, data: IFilter) => any;

	constructor(uniqueIdent: number, forwardData: IFilter, forwardFunc: (uniqueIdent: number, data: IFilter) => any, backwardData: IFilter, backwardFunc: (uniqueIdent: number, data: IFilter) => any) {
		super('update filter');
		this.uniqueIdent = uniqueIdent;
		this.backwardData = backwardData;
		this.backwardFunc = backwardFunc;
		this.forwardData = forwardData;
		this.forwardFunc = forwardFunc;
	}

	async redo(): Promise<void> {
		await this.forwardFunc(this.uniqueIdent, this.forwardData);
	}

	async undo(): Promise<void> {
		await this.backwardFunc(this.uniqueIdent, this.backwardData);
	}
}

// FIXME: NOT SAFE!!
function generateUniqueIdent(checkMap: Map<number, any>): number {
	let ident: number = Math.trunc(Math.random() * 100000);
	while (checkMap.has(ident)) {
		ident = Math.trunc(Math.random() * 100000);
	}
	return ident;
}
